/**
 * SimpleAudioIndicator Component
 * 
 * A lightweight audio indicator that shows when audio is active
 * with minimal performance impact. Works with both input and output audio.
 */

import React, { useEffect, useState } from 'react';
import './styles.scss';

interface SimpleAudioIndicatorProps {
  /** Whether audio is active */
  isActive?: boolean;
  /** The output volume level (0-1) */
  outputVolume?: number;
  /** The input volume level (0-1) */
  inputVolume?: number;
  /** Additional class names */
  className?: string;
  /** Size of the indicator in pixels */
  size?: number;
  /** Mode of visualization */
  mode?: 'input' | 'output' | 'both';
}

const SimpleAudioIndicator: React.FC<SimpleAudioIndicatorProps> = ({
  isActive = false,
  outputVolume = 0,
  inputVolume = 0,
  className = '',
  size = 24,
  mode = 'both',
}) => {
  const [inputBars, setInputBars] = useState<number[]>([0.2, 0.2, 0.2]);
  const [outputBars, setOutputBars] = useState<number[]>([0.2, 0.2, 0.2]);
  
  // Update input bar heights based on input volume
  useEffect(() => {
    if (isActive && (mode === 'input' || mode === 'both')) {
      // Simple algorithm to create varied bar heights for input
      const newBars = [
        Math.min(1, 0.3 + inputVolume * 0.7),
        Math.min(1, 0.5 + inputVolume * 0.5),
        Math.min(1, 0.4 + inputVolume * 0.6)
      ];
      setInputBars(newBars);
    } else {
      setInputBars([0.2, 0.2, 0.2]);
    }
  }, [isActive, inputVolume, mode]);
  
  // Update output bar heights based on output volume
  useEffect(() => {
    if (isActive && (mode === 'output' || mode === 'both')) {
      // Simple algorithm to create varied bar heights for output
      const newBars = [
        Math.min(1, 0.3 + outputVolume * 0.7),
        Math.min(1, 0.5 + outputVolume * 0.5),
        Math.min(1, 0.4 + outputVolume * 0.6)
      ];
      setOutputBars(newBars);
    } else {
      setOutputBars([0.2, 0.2, 0.2]);
    }
  }, [isActive, outputVolume, mode]);

  // Determine which bars to show based on mode
  const showInputBars = mode === 'input' || mode === 'both';
  const showOutputBars = mode === 'output' || mode === 'both';
  
  // Determine if any audio is active
  const hasInputActivity = inputVolume > 0.01 && showInputBars;
  const hasOutputActivity = outputVolume > 0.01 && showOutputBars;
  const audioActive = isActive && (hasInputActivity || hasOutputActivity);
  
  return (
    <div 
      className={`simple-audio-indicator ${className} ${audioActive ? 'active' : 'inactive'}`}
      style={{ width: size, height: size }}
    >
      {/* Input bars (left side) */}
      {showInputBars && (
        <div className="input-bars">
          {inputBars.map((height, index) => (
            <div 
              key={`input-${index}`} 
              className={`audio-bar input-bar ${hasInputActivity ? 'active' : ''}`}
              style={{ 
                height: `${Math.max(15, height * 100)}%`,
                animationDelay: `${index * 0.1}s`
              }}
            />
          ))}
        </div>
      )}
      
      {/* Output bars (right side) */}
      {showOutputBars && (
        <div className="output-bars">
          {outputBars.map((height, index) => (
            <div 
              key={`output-${index}`} 
              className={`audio-bar output-bar ${hasOutputActivity ? 'active' : ''}`}
              style={{ 
                height: `${Math.max(15, height * 100)}%`,
                animationDelay: `${index * 0.1}s`
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SimpleAudioIndicator;
